import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  doc,
  getDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { format } from 'date-fns';
import { useConsignorId } from '../hooks/useConsignorId';
import { useAuth } from '../contexts/AuthContext';
import { ConsignmentContract, ConsignmentOrder, SettlementReport } from '../types/consignment.types';

// ─── Status badge ─────────────────────────────────────────────────────────────

type AnyStatus = string;
const STATUS_CLASSES: Record<string, string> = {
  draft:             'bg-gray-100 text-gray-700',
  pending_signature: 'bg-yellow-100 text-yellow-800',
  active:            'bg-green-100 text-green-800',
  settled:           'bg-blue-100 text-blue-800',
  cancelled:         'bg-red-100 text-red-700',
  receiving:         'bg-orange-100 text-orange-800',
  processing:        'bg-yellow-100 text-yellow-800',
  cataloged:         'bg-sky-100 text-sky-800',
  live:              'bg-green-100 text-green-800',
  paid:              'bg-green-100 text-green-800',
  unpaid:            'bg-red-100 text-red-700',
};

const StatusBadge: React.FC<{ status: AnyStatus }> = ({ status }) => (
  <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${STATUS_CLASSES[status] ?? 'bg-gray-100 text-gray-700'}`}>
    {status.replace(/_/g, ' ')}
  </span>
);

// ─── Section wrapper ──────────────────────────────────────────────────────────

const Section: React.FC<{
  title: string;
  count: number;
  children: React.ReactNode;
  emptyMessage: string;
  isEmpty: boolean;
}> = ({ title, count, children, emptyMessage, isEmpty }) => (
  <div className="card space-y-4">
    <div className="flex items-center justify-between">
      <h2 className="text-xl font-bold text-gray-900">{title}</h2>
      <span className="text-sm text-gray-500">{count} total</span>
    </div>
    {isEmpty ? (
      <div className="py-10 text-center text-gray-400">
        <svg className="mx-auto mb-3 w-10 h-10 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p className="text-sm">{emptyMessage}</p>
      </div>
    ) : (
      children
    )}
  </div>
);

// ─── Component ────────────────────────────────────────────────────────────────

interface ContractRow {
  id: string;
  consignorId?: string;
  consignorName?: string;
  status?: string;
  createdAt?: Date;
  [key: string]: any; // Allow other properties
}

interface CORow {
  id: string;
  coId?: string;
  consignorId?: string;
  status?: string;
  /** Legacy flat field. NOT in ConsignmentOrder — no writer has ever emitted it. */
  totalItemsReceived?: number;
  /** The shape the type actually declares (consignment.types.ts:156-164). */
  totals?: { itemsReceived?: number };
  /** Last resort: the embedded item array, when a CO has been populated. */
  items?: unknown[];
  createdAt?: Date;
  [key: string]: any;
}

/**
 * Item count for a CO, in order of trustworthiness. Returns undefined when
 * nothing knows — deliberately, so the caller renders NOTHING rather than
 * "0 items received". A hard zero on a consignor's own dashboard is a false
 * statement about their property; an absent line is merely quiet.
 *
 * Was: `co.totalItemsReceived ?? 0`, reading a field the type does not declare
 * and no writer emits — so every CO on every consignor dashboard read "0".
 */
function itemsReceivedOf(co: CORow): number | undefined {
  const t = co.totals?.itemsReceived;
  if (typeof t === 'number') return t;
  if (typeof co.totalItemsReceived === 'number') return co.totalItemsReceived;
  if (Array.isArray(co.items)) return co.items.length;
  return undefined;
}

interface SettlementRow {
  id: string;
  generatedAt?: Date;
  paymentDueDate?: Date;
  totals?: {
    netPayment?: number;
  };
  paymentInfo?: {
    paidAt?: Date;
  };
  [key: string]: any;
}

const ConsignorDashboard: React.FC<{ consignorIdOverride?: string; basePath?: string }> = ({
  consignorIdOverride,
  basePath = '/consignor',
}) => {
  const { currentUser } = useAuth();
  const { consignorId, loading: idLoading } = useConsignorId(consignorIdOverride);

  const [consignorName, setConsignorName] = useState<string>('');
  const [contracts, setContracts] = useState<ContractRow[]>([]);
  const [cos, setCos] = useState<CORow[]>([]);
  const [settlements, setSettlements] = useState<SettlementRow[]>([]);
  const [dataLoading, setDataLoading] = useState(false);

  useEffect(() => {
    if (!consignorId) return;
    loadAll(consignorId);
  }, [consignorId]);

  const loadAll = async (cid: string) => {
    setDataLoading(true);

    try {
      // Load consignor name
      const consignorDoc = await getDoc(doc(db, 'consignors', cid));
      if (consignorDoc.exists()) {
        setConsignorName(consignorDoc.data().name || 'Consignor');
      }

      // Load contracts
      const contractSnap = await getDocs(query(
        collection(db, 'consignmentContracts'),
        where('consignorId', '==', cid),
        orderBy('createdAt', 'desc'),
      ));

      // Load COs
      const coSnap = await getDocs(query(
        collection(db, 'consignmentOrders'),
        where('consignorId', '==', cid),
        orderBy('createdAt', 'desc'),
      ));

      // Load settlements
      const settlementSnap = await getDocs(collection(db, 'consignors', cid, 'settlements'));

      setContracts(contractSnap.docs.map(d => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate?.() || undefined,
      })));

      setCos(coSnap.docs.map(d => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate?.() || undefined,
      })));

      setSettlements(settlementSnap.docs.map(d => ({
        id: d.id,
        ...d.data(),
        generatedAt: d.data().generatedAt?.toDate?.() || undefined,
      })));

    } catch (err: any) {
      console.error('Error loading portal data:', err);
    } finally {
      setDataLoading(false);
    }
  };

  const loading = idLoading || dataLoading;

  return (
    <div className="max-w-5xl mx-auto space-y-8">

      {/* Header with Welcome */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg shadow-lg p-6 text-white">
        <h1 className="text-3xl font-bold">
          Welcome{consignorName ? `, ${consignorName}` : ''}! 👋
        </h1>
        <p className="text-primary-100 mt-2">
          {currentUser?.email ? `Logged in as: ${currentUser.email}` : 'View your contracts, consignment orders, and settlement statements.'}
        </p>
        {consignorId && (
          <p className="text-xs text-primary-200 mt-1">
            Consignor ID: {consignorId}
          </p>
        )}
      </div>

      {loading ? (
        <div className="card py-20 text-center text-gray-500">
          <div className="inline-block w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mb-3" />
          <p>Loading your account…</p>
        </div>
      ) : (
        <>
          {/* ── Contracts ────────────────────────────────────────────────── */}
          <Section
            title="My Contracts"
            count={contracts.length}
            isEmpty={contracts.length === 0}
            emptyMessage="No contracts yet. Contact Schmidt Auctions to get started."
          >
            <div className="space-y-3">
              {contracts.map(c => (
                <Link
                  key={c.id}
                  to={`${basePath}/contracts/${c.id}`}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition group"
                >
                  <div>
                    <p className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                      {c.id}
                    </p>
                    <p className="text-sm text-gray-500 mt-0.5">
                      {c.consignorName}
                      {c.createdAt ? ` · ${format(c.createdAt, 'MMM d, yyyy')}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {c.status && <StatusBadge status={c.status} />}
                    <svg className="w-4 h-4 text-gray-400 group-hover:text-primary-600 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              ))}
            </div>
          </Section>

          {/* ── Consignment Orders ───────────────────────────────────────── */}
          <Section
            title="My Consignment Orders"
            count={cos.length}
            isEmpty={cos.length === 0}
            emptyMessage="No consignment orders yet. Orders are created after your contract is activated."
          >
            <div className="space-y-3">
              {cos.map(co => (
                <Link
                  key={co.id}
                  to={`${basePath}/orders/${co.id}`}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition group"
                >
                  <div>
                    <p className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                      {co.coId || co.id}
                    </p>
                    <p className="text-sm text-gray-500 mt-0.5">
                      {(() => {
                        const n = itemsReceivedOf(co);
                        return [
                          n != null ? `${n} item${n === 1 ? '' : 's'} received` : null,
                          co.createdAt ? format(co.createdAt, 'MMM d, yyyy') : null,
                        ].filter(Boolean).join(' · ');
                      })()}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {co.status && <StatusBadge status={co.status} />}
                    <svg className="w-4 h-4 text-gray-400 group-hover:text-primary-600 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              ))}
            </div>
          </Section>

          {/* ── Settlements ──────────────────────────────────────────────── */}
          <Section
            title="Settlement Statements"
            count={settlements.length}
            isEmpty={settlements.length === 0}
            emptyMessage="Settlements appear here after your auction concludes and the 21-day period completes."
          >
            <div className="space-y-3">
              {settlements.map(s => (
                <Link
                  key={s.id}
                  to={`${basePath}/settlements/${s.id}`}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition group"
                >
                  <div>
                    <p className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                      Settlement {s.id}
                    </p>
                    <p className="text-sm text-gray-500 mt-0.5">
                      {s.generatedAt ? format(s.generatedAt, 'MMM d, yyyy') : ''}
                      {s.paymentDueDate
                        ? ` · Due ${format((s.paymentDueDate as unknown as Date), 'MMM d, yyyy')}`
                        : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    {s.totals?.netPayment != null && (
                      <span className="text-lg font-bold text-green-600">
                        ${s.totals.netPayment.toFixed(2)}
                      </span>
                    )}
                    {s.paymentInfo?.paidAt
                      ? <StatusBadge status="paid" />
                      : <StatusBadge status="unpaid" />
                    }
                    <svg className="w-4 h-4 text-gray-400 group-hover:text-primary-600 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              ))}
            </div>
          </Section>
        </>
      )}
    </div>
  );
};

export default ConsignorDashboard;